import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@xemplo/auth-provider';
import { AMENDMENT_QUERY_KEYS } from '@xemplo/gp-constants';
import { useSignalRHub } from '@xemplo/signalr';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import { AmendmentHubAPI, AmendmentHubEvents } from './amendment-query.constants';
import {
  AmendmentStatusNotification,
  UseAmendmentHubOptions,
  UseAmendmentHubResult,
} from './use-amendment-hub.types';

const AMENDMENT_INVALIDATION_KEYS = [
  AMENDMENT_QUERY_KEYS.GET_AMENDMENTS,
  AMENDMENT_QUERY_KEYS.GET_AMENDMENT_BY_ID,
];

export function useAmendmentHub(options?: UseAmendmentHubOptions): UseAmendmentHubResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { signalRUrl } = useAppSettings();
  const amendmentId = options?.amendmentId;
  const payrunId = options?.payrunId;
  const onStatusChanged = options?.onStatusChanged;
  // Serialise to a stable string so the array itself isn't a useEffect dep
  const amendmentIdsKey = options?.amendmentIds?.map(String).join(',') ?? '';

  const hubUrl = `${signalRUrl}/${AmendmentHubAPI.hub}`;

  // connection is null until the WebSocket handshake completes.
  // reconnectCount increments on each automatic reconnect — used to re-subscribe below.
  const { connection, connectionState, reconnectCount } = useSignalRHub({
    hubUrl,
    getAccessToken: () => user?.access_token,
  });

  // Ref so the event handler below always calls the latest callback without re-registering.
  const onStatusChangedRef = useRef(onStatusChanged);
  useEffect(() => {
    onStatusChangedRef.current = onStatusChanged;
  });

  // invoke() calls a named method on the hub server, joining this client to a SignalR group.
  // reconnectCount in deps ensures we re-subscribe after a reconnect (server drops groups on disconnect).
  useEffect(() => {
    if (!connection || amendmentId == null) return;

    connection
      .invoke(AmendmentHubAPI.subscribe.toAmendment, String(amendmentId))
      .catch((e) => console.warn('[useAmendmentHub] subscribe toAmendment failed', e));

    return () => {
      connection
        .invoke(AmendmentHubAPI.unsubscribe.fromAmendment, String(amendmentId))
        .catch((e) =>
          console.warn('[useAmendmentHub] unsubscribe fromAmendment failed', e),
        );
    };
  }, [connection, reconnectCount, amendmentId]);

  useEffect(() => {
    if (!connection || !amendmentIdsKey) return;

    const ids = amendmentIdsKey ? amendmentIdsKey.split(',') : [];
    connection
      .invoke(AmendmentHubAPI.subscribe.toAmendments, ids)
      .catch((e) => console.warn('[useAmendmentHub] subscribe toAmendments failed', e));

    return () => {
      connection
        .invoke(AmendmentHubAPI.unsubscribe.fromAmendments, ids)
        .catch((e) =>
          console.warn('[useAmendmentHub] unsubscribe fromAmendments failed', e),
        );
    };
  }, [connection, reconnectCount, amendmentIdsKey]);

  useEffect(() => {
    if (!connection || payrunId == null) return;

    connection
      .invoke(AmendmentHubAPI.subscribe.toPayRun, String(payrunId))
      .catch((e) => console.warn('[useAmendmentHub] subscribe toPayRun failed', e));

    return () => {
      connection
        .invoke(AmendmentHubAPI.unsubscribe.fromPayRun, String(payrunId))
        .catch((e) => console.warn('[useAmendmentHub] unsubscribe fromPayRun failed', e));
    };
  }, [connection, reconnectCount, payrunId]);

  // connection.on registers a listener for a server-push event (like addEventListener).
  // Add more connection.on calls here as the BE adds new event types.
  useEffect(() => {
    if (!connection) return;

    const handleStatusChanged = (notification: AmendmentStatusNotification) => {
      AMENDMENT_INVALIDATION_KEYS.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [key] });
      });
      onStatusChangedRef.current?.(notification);
    };

    connection.on(AmendmentHubEvents.StatusChanged, handleStatusChanged);

    return () => {
      connection.off(AmendmentHubEvents.StatusChanged, handleStatusChanged);
    };
  }, [connection, queryClient]);

  return { connectionState };
}
